import _ from 'lodash';
import React, { SyntheticEvent, useEffect, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import CustomModal from '../CustomModal/CustomModal';
import { CashAccountSelector, refreshInput, setAccount, setCashAccounts, setCountry, setCurrency, setDatatableSelectedCashAccountIndex, setInput, setShowCashAccount, setTriggerRefreshTable } from './reducers/CashAccountReducer';
import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, Button, Grid, TextField, Tooltip } from '@mui/material';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import CashAccountApi, { DeleteCashAccountByIdParam } from './api/CashAccountApi';
import BaseResponse from '../../app/BaseResponse';
import CashAccountModel from './models/CashAccountModel';
import Utility from '../../app/utility';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import Swal from 'sweetalert2';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';

const CashAccountModal: React.FunctionComponent = (props) => {
    const dispatch = useDispatch();
    const cashModelData = useSelector(CashAccountSelector);

    useEffect(() => {
        if (cashModelData.cashAccounts.length == 0 || cashModelData.triggerRefreshTable > 0) {
            dispatch(showLoading(true));
            CashAccountApi.GetCashAccounts().then((response: BaseResponse<CashAccountModel[]>) => {
                if (response.success) {
                    dispatch(setCashAccounts(response.data));

                    dispatch(showLoading(false));

                    dispatch(setTriggerRefreshTable(0));
                } else {
                    Utility.Alert.Error(response.message);
                    dispatch(showLoading(false));
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
                dispatch(showLoading(false));
            })
        }
    }, [cashModelData.triggerRefreshTable]);

    let save = () => {
        try {
            validate();

            CashAccountApi.Save(cashModelData.input)
                .then((response: BaseResponse<CashAccountModel>) => {
                    if (response.success) {
                        if (cashModelData.input.id.length === 0) {
                            Utility.Alert.Success("Cash account added successful");
                        } else {
                            Utility.Alert.Success("Cash account updated successful");
                        }

                        dispatch(refreshInput());

                        dispatch(setTriggerRefreshTable(new Date().getTime()));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (cashModelData.input.country.length === 0) {
            throw "Country is required";
        }

        if (cashModelData.input.account.length === 0) {
            throw "Account is required";
        }

        if (cashModelData.input.currency.length === 0) {
            throw "Currency is required";
        }
    }

    let getAccountByCountry = () => {
        if (cashModelData.cashAccounts.length == 0 || !cashModelData.input.country || cashModelData.input.country.length == 0) {
            return [] as string[];
        } else {
            let accounts = [] as string[];
            _.each(cashModelData.cashAccounts, (cashAccount) => {
                if (cashAccount.country == cashModelData.input.country) {
                    accounts.push(cashAccount.account);
                }
            });
            return accounts;
        }
    }

    const columns = [
        {
            Field: Utility.propertyOf<CashAccountModel>("country"),
            Title: "Country",
            AllowSort: false,
        } as DatatableColumnModel<CashAccountModel>,
        {
            Field: Utility.propertyOf<CashAccountModel>("account"),
            Title: "Account",
            AllowSort: false,
        } as DatatableColumnModel<CashAccountModel>,
        {
            Field: Utility.propertyOf<CashAccountModel>("currency"),
            Title: "Currency",
            AllowSort: false,
        } as DatatableColumnModel<CashAccountModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: CashAccountModel, index: number) => (
                <>
                    <Tooltip title="Delete Cash Account">
                        <Button
                            className='table-action-button'
                            variant='outlined'
                            color='error'
                            onClick={() => {
                                deleteCashAccount(rowData.id)
                            }}
                        >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                        </Button>
                    </Tooltip>
                </>
            )
        } as DatatableColumnModel<CashAccountModel>,
    ];

    const deleteCashAccount = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                CashAccountApi.Delete({
                    id: id
                } as DeleteCashAccountByIdParam)
                    .then((response: BaseResponse<CashAccountModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Cash account deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerRefreshTable(new Date().getTime()));
                        }
                    });
            }
        })
    }

    return (
        <>
            <CustomModal
                open={cashModelData.showCashAccountModel}
                onClose={() => {
                    dispatch(setShowCashAccount(false));
                }}
                className='cash-account-modal'
            >
                <Grid container>
                    <Grid item lg={3} xs={12}>
                        <Grid container className='cargo-input-component'>
                            <Grid xs={10} lg={9}>
                                <Autocomplete
                                    className='cargo-input-component'
                                    options={["Vietnam", "Korea", "Singapore", "BVI"]}
                                    renderInput={(params) => <TextField {...params} label="Country" />}
                                    value={cashModelData.input.country}
                                    onChange={(event: SyntheticEvent<Element, Event>, value: string | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined) => {
                                        dispatch(setCountry(value ? value : ""));
                                    }}
                                />
                            </Grid>
                            <Grid item xs={2} lg={3}>
                                <Button
                                    className="clear-input-button"
                                    tabIndex={-1}
                                    color="success"
                                    variant="contained"
                                    onClick={() => {
                                        dispatch(refreshInput());
                                    }}
                                >
                                    <i className="fa fa-refresh" aria-hidden="true"></i>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Autocomplete
                            className='cargo-input-component'
                            freeSolo={true}
                            options={getAccountByCountry()}
                            renderInput={(params) => <TextField {...params} label="Account" />}
                            value={cashModelData.input.account}
                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setAccount(value ? value : ""));
                            }}
                            onInputChange={(event: React.SyntheticEvent, value: string, reason: string) => {
                                dispatch(setAccount(value ? value : ""));
                            }}
                        />
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Autocomplete
                            className='cargo-input-component'
                            options={["VND", "USD"]}
                            renderInput={(params) => <TextField {...params} label="Currency" />}
                            value={cashModelData.input.currency}
                            onChange={(event: SyntheticEvent<Element, Event>, value: string | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setCurrency(value ? value : ""));
                            }}
                        />
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Button
                            onClick={save}
                            variant="outlined"
                        >
                            Save (s)
                        </Button>
                    </Grid>
                </Grid>
                <Grid container>
                    <div className='datatable-wrapper'>
                        <Datatable<CashAccountModel>
                            useCheckBox={false}
                            useDefaultSearchBar={false}
                            pagingType={PagingTypeEnum.None}
                            data={cashModelData.cashAccounts}
                            useItemPerPage={false}
                            columns={columns}
                            isDataLoaded={true}
                            className={"care-datatable-search-result nice-scroll"}
                            useHeaderSort={true}
                            onRowSelectedChange={(cashAccount: CashAccountModel, index: number, event: MouseEvent<HTMLElement>) => {
                                var target = event.target as HTMLElement;
                                if (target.tagName !== "BUTTON") {
                                    dispatch(setDatatableSelectedCashAccountIndex(index));
                                    dispatch(setInput({ ...cashAccount }));
                                }
                            }}
                            selectedRowIndex={cashModelData.datatableSelectedIndex}
                            stickyHeader={true}
                        ></Datatable>
                    </div>
                </Grid>
            </CustomModal>
        </>
    )
};

export default CashAccountModal;